import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Home, Inbox, MessageSquare, ArrowUpRight, Clock, CalendarCheck, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LMS_URL } from "../lib/config";

const StatusPill = ({ status }) => {
  const map = { new: "text-cyan-400 border-cyan-400/40", "in-progress": "text-amber-300 border-amber-300/40", closed: "text-white/40 border-white/20" };
  return <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 rounded-full ${map[status] || map.new}`}>{status}</span>;
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/inquiries/me").then(({ data }) => setInquiries(data)).catch(() => {}),
      api.get("/bookings/me").then(({ data }) => setBookings(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <header className="border-b border-white/10 bg-[#050505]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl text-white">Ai<span className="text-cyan-400">.</span>Sho</Link>
          <div className="flex items-center gap-6">
            {LMS_URL ? (
              <a href={LMS_URL} target="_blank" rel="noopener noreferrer" data-testid="dashboard-lms-launch"
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400 hover:text-white transition-colors">
                <ExternalLink size={14} /> Launch LMS
              </a>
            ) : (
              <span data-testid="dashboard-lms-soon" title="Launching soon"
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/30 cursor-not-allowed">
                <ExternalLink size={14} /> LMS Soon
              </span>
            )}
            <Link to="/" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 transition-colors" data-testid="dashboard-home-link">
              <Home size={14} /> Site
            </Link>
            <button onClick={logout} data-testid="dashboard-logout" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-cyan-400 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400 mb-4">Client Dashboard</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white leading-none" data-testid="dashboard-welcome">
            Welcome, {user.name?.split(" ")[0] || "Client"}.
          </h1>
          <p className="mt-4 text-white/50 max-w-lg">Track your engagements with AiSho and pick up the conversation anytime.</p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
          {[
            { icon: Inbox, n: inquiries.length, l: "Total Inquiries" },
            { icon: CalendarCheck, n: bookings.length, l: "Consultations" },
            { icon: Clock, n: inquiries.filter((i) => i.status === "new").length, l: "Awaiting Review" },
          ].map((s) => (
            <div key={s.l} className="bg-[#0C0C0C] p-8">
              <s.icon size={22} strokeWidth={1.25} className="text-cyan-400 mb-5" />
              <p className="font-serif text-4xl text-white">{s.n}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mt-2">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <h2 className="font-serif text-3xl text-white mb-8">Your Consultations</h2>
          {loading ? (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 animate-pulse">Loading…</p>
          ) : bookings.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-sm p-12 text-center" data-testid="dashboard-bookings-empty">
              <CalendarCheck size={30} strokeWidth={1.25} className="text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No consultations booked yet.</p>
            </div>
          ) : (
            <div className="space-y-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
              {bookings.map((b) => {
                const bColor = { requested: "text-cyan-400 border-cyan-400/40", confirmed: "text-emerald-300 border-emerald-300/40", rescheduled: "text-amber-300 border-amber-300/40", cancelled: "text-red-400 border-red-400/40" }[b.status] || "text-white/40 border-white/20";
                return (
                  <div key={b.booking_id} className="bg-[#0C0C0C] p-6 md:p-7 flex items-center justify-between gap-4" data-testid={`dashboard-booking-${b.booking_id}`}>
                    <div>
                      <div className="flex items-center gap-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">{b.service || "General"}</p>
                        <span className={`font-mono text-[9px] uppercase tracking-[0.15em] border px-2 py-0.5 rounded-full ${bColor}`}>{b.status}</span>
                      </div>
                      {b.message && <p className="text-white/70 text-sm mt-2 max-w-xl">{b.message}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-serif text-2xl text-white">{b.date}</p>
                      <p className="font-mono text-[11px] text-cyan-400">{b.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-serif text-3xl text-white">Your Inquiries</h2>
            <Link to="/#contact" className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-2 hover:gap-3 transition-all" data-testid="dashboard-new-inquiry">
              New Inquiry <ArrowUpRight size={14} />
            </Link>
          </div>

          {loading ? (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40 animate-pulse">Loading…</p>
          ) : inquiries.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-sm p-16 text-center" data-testid="dashboard-empty">
              <Inbox size={32} strokeWidth={1.25} className="text-white/20 mx-auto mb-5" />
              <p className="text-white/50">No inquiries yet.</p>
              <Link to="/#contact" className="mt-4 inline-flex items-center gap-2 text-cyan-400 hover:underline text-sm">
                Submit your first inquiry <ArrowUpRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
              {inquiries.map((iq) => (
                <div key={iq.lead_id} className="bg-[#0C0C0C] p-6 md:p-8 hover:bg-[#101010] transition-colors" data-testid={`inquiry-${iq.lead_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-2">{iq.service || "General"}</p>
                      <p className="text-white/85 leading-relaxed max-w-2xl">{iq.message}</p>
                      <p className="font-mono text-[10px] text-white/30 mt-4">{new Date(iq.created_at).toLocaleString()}</p>
                    </div>
                    <StatusPill status={iq.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
